/**
 * What keeps a windowed fundraising table still while its rows come and go (`useWindowedRows`).
 *
 * - **Fixed columns.** The table is `table-fixed` and its widths are set once, in a `<colgroup>` that both the
 *   heading and every row follow. An auto-laid-out table sizes its columns from the rows mounted, so the columns
 *   jumped as the window moved; now a column is as wide as it's set, and what doesn't fit truncates. The column
 *   left without a width (the person) takes the rest.
 * - **Pinned headings.** Each heading sticks to the top of the page scroller (the admin shell's `<main>`), on the
 *   card's background with its own bottom rule, above the rows. Nothing between the table and that scroller may
 *   be a scroll box — an `overflow-x-auto` or `overflow-hidden` wrapper would pin the headings to itself, which
 *   never scrolls, so the card clips its corners with `overflow-clip` instead and a narrow window scrolls the
 *   page sideways rather than the table.
 */

/** The classes that pin a heading cell to the top of the page scroller — under the list's loading line (z-20). */
export const STICKY_HEADING = 'sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_var(--color-border)]';

/** A table's column widths, in order — a Tailwind width class, or null for the column that takes the rest. */
export function ColumnWidths({ widths }: { widths: readonly (string | null)[] }) {
  return (
    <colgroup>
      {widths.map((width, index) => (
        <col key={index} className={width ?? undefined} />
      ))}
    </colgroup>
  );
}
